"use client";

import type { ColumnDef } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import Image from "next/image";

import { ListingStatus } from "@/.zenstack/logical-prisma-client";
import { CommentsModal } from "@/components/comments/CommentsModal";
import { ListingLink } from "@/components/ListingLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { type ListingDetailedType } from "@/hooks/use-listings";

import { type ListingWithCommentsAndReplies } from "../comments/Comments";

// Shared column definitions
export const sharedColumns: ColumnDef<ListingDetailedType>[] = [
  {
    id: "property",
    header: ({ column }) => {
      return (
        <Button
          variant="link"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-m flex items-center gap-2 font-mono text-xs font-medium"
        >
          Property
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    cell: ({ row }) => {
      const listing = row.original;
      return (
        <ListingLink listing={listing}>
          <div className="flex items-center gap-3">
            {listing.files && listing.files.length > 0 && (
              <Image
                src={listing.files[0]?.url ?? ""}
                alt={listing.title}
                width={40}
                height={40}
                className="size-10 rounded-md object-cover"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{listing.title}</div>
              <div className="font-geist truncate font-normal text-muted-foreground">
                {listing.details?.address?.streetLine1},{" "}
                {listing.details?.address?.city}
              </div>
            </div>
          </div>
        </ListingLink>
      );
    },
    sortingFn: (a, b) => {
      const aAddress = `${a.original.details?.address?.streetLine1 || ""} ${a.original.details?.address?.city || ""}`;
      const bAddress = `${b.original.details?.address?.streetLine1 || ""} ${b.original.details?.address?.city || ""}`;
      return aAddress.localeCompare(bAddress);
    },
  },
  {
    id: "startingPrice",
    header: ({ column }) => {
      return (
        <Button
          variant="link"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 font-mono text-xs font-medium"
        >
          Starting Price
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    cell: ({ row }) => {
      const listing = row.original;
      const startingBid = Number(listing.atAuction?.startingBid ?? 0);
      return (
        <span className="font-mono text-xs font-medium">
          ${startingBid.toLocaleString()}
        </span>
      );
    },
    sortingFn: (a, b) =>
      Number(a.original.atAuction?.startingBid ?? 0) -
      Number(b.original.atAuction?.startingBid ?? 0),
  },
  {
    id: "reservePrice",
    header: ({ column }) => {
      return (
        <Button
          variant="link"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 rounded-md font-mono text-xs font-medium"
        >
          Reserve Price
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    cell: ({ row }) => {
      const listing = row.original;
      const reserve = listing.atAuction?.reserveBidAmount;
      return reserve ? (
        <span className="font-medium">${Number(reserve).toLocaleString()}</span>
      ) : (
        <span className="font-mono text-xs font-medium text-muted-foreground">
          None
        </span>
      );
    },
    sortingFn: (a, b) =>
      Number(a.original.atAuction?.reserveBidAmount || 0) -
      Number(b.original.atAuction?.reserveBidAmount || 0),
  },
  {
    id: "highestBid",
    header: ({ column }) => {
      return (
        <Button
          variant="link"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 font-mono text-xs font-medium"
        >
          Highest Bid
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    cell: ({ row }) => {
      const listing = row.original;
      const highestBid = listing.atAuction?.bids?.[0];
      return highestBid ? (
        <span className="font-medium">
          ${Number(highestBid.currentBidAmount).toLocaleString()}
        </span>
      ) : (
        <span className="font-mono text-xs font-medium text-muted-foreground">
          No bids
        </span>
      );
    },
    sortingFn: (a, b) => {
      const aHighestBid =
        a.original.atAuction?.bids?.[0]?.currentBidAmount || 0;
      const bHighestBid =
        b.original.atAuction?.bids?.[0]?.currentBidAmount || 0;
      return Number(aHighestBid) - Number(bHighestBid);
    },
  },
  {
    id: "winningBidder",
    header: () => (
      <span className="font-mono text-xs font-medium">Winning Bidder</span>
    ),
    cell: ({ row }) => {
      const listing = row.original;
      const winningBid = listing.atAuction?.winningBid;

      if (!winningBid) {
        return (
          <span className="font-mono text-xs font-medium text-muted-foreground">
            -
          </span>
        );
      }

      if (winningBid) {
        try {
          const userName = winningBid.propertySeeker?.user?.name;
          if (userName) {
            return (
              <span className="font-mono text-xs font-medium">{userName}</span>
            );
          }
        } catch (_e) {
          // ignore malformed bid shape
        }
      }

      return (
        <span className="font-mono text-xs font-medium text-muted-foreground">
          -
        </span>
      );
    },
  },
  {
    id: "bidCount",
    header: ({ column }) => {
      return (
        <Button
          variant="link"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 font-mono text-xs font-medium"
        >
          Bid Count
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    cell: ({ row }) => {
      const listing = row.original;
      return (
        <>
          {listing.atAuction ? (
            <span className="font-mono text-xs font-medium">
              {listing.atAuction.bids?.length ?? 0}
            </span>
          ) : (
            <span className="font-mono text-xs font-medium text-muted-foreground">
              -
            </span>
          )}
        </>
      );
    },
    sortingFn: (a, b) =>
      (a.original.atAuction?.bids?.length ?? 0) -
      (b.original.atAuction?.bids?.length ?? 0),
  },
  {
    id: "comments",
    header: () => (
      <span className="font-mono text-xs font-medium">Comments</span>
    ),
    cell: ({ row }) => {
      const listing = row.original;
      // Since we're using any types, be defensive about the structure
      const comments = listing.comments || [];
      let totalReplies = 0;

      try {
        totalReplies = comments.reduce((acc: number, comment: any) => {
          const replies = comment.replies || [];
          return acc + replies.length;
        }, 0);
      } catch (_e) {
        // Fall back to 0 if there's an error
        totalReplies = 0;
      }

      return (
        <CommentsModal
          comments={comments as unknown as ListingWithCommentsAndReplies[]} // TODO: Verify this is correct
          trigger={
            <span className="cursor-pointer font-mono text-xs font-medium text-foreground hover:underline">
              {comments.length + totalReplies}
            </span>
          }
        />
      );
    },
  },
  {
    id: "status",
    header: ({ column }) => {
      return (
        <Button
          variant="link"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 font-mono text-xs font-medium"
        >
          Status
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    cell: ({ row }) => {
      const listing = row.original as ListingDetailedType;
      const status = listing.status;

      const getStatusBadgeVariant = (status: ListingStatus) => {
        switch (status) {
          case ListingStatus.DRAFT:
            return "secondary";
          case ListingStatus.PENDING:
            return "outline";
          case ListingStatus.ACTIVE:
            return "default";
          case ListingStatus.PAUSED:
            return "secondary";
          case ListingStatus.STOPPED:
            return "destructive";
          case ListingStatus.CLOSED:
            return "secondary";
          case ListingStatus.HIDDEN:
            return "outline";
          case ListingStatus.ARCHIVED:
            return "outline";
          default:
            return "outline";
        }
      };

      return <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>;
    },
    sortingFn: (a, b) => {
      const aStatus = a.original.status;
      const bStatus = b.original.status;
      return aStatus.localeCompare(bStatus);
    },
  },
];

export const propertyColumn: ColumnDef<ListingDetailedType> = {
  id: "property",
  header: () => <span className="font-mono text-xs font-medium">Property</span>,
  cell: ({ row }) => {
    const listing = row.original;
    return (
      <div className="flex items-center gap-4">
        <div className="relative h-16 w-16 overflow-hidden rounded-lg">
          {listing.files &&
          listing.files.length > 0 &&
          listing.files[0]?.url ? (
            <Image
              src={listing.files[0]?.url ?? ""}
              alt={listing.title}
              fill
              className="object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-xs text-muted-foreground">No image</span>
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{listing.title}</div>
          <div className="truncate text-sm font-normal text-muted-foreground">
            {listing.details?.housingDetails?.bedrooms}{" "}
            {listing.details?.housingDetails?.bedrooms === "1" ? "bed" : "beds"}{" "}
            • {listing.details?.housingDetails?.bathrooms}{" "}
            {listing.details?.housingDetails?.bathrooms === "1"
              ? "bath"
              : "baths"}
            {listing.details?.housingDetails?.squareFootage && (
              <>
                {" "}
                •{" "}
                {listing.details.housingDetails.squareFootage.toLocaleString()}{" "}
                sq ft
              </>
            )}
          </div>
          <div className="truncate text-sm font-normal text-muted-foreground">
            {listing.details?.address?.streetLine1},{" "}
            {listing.details?.address?.city}
          </div>
        </div>
      </div>
    );
  },
};
